import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAdminInterviewGraphQL,
  useAdminInterviewSummaryItemsGraphQL,
  useAdminSummaryCheckMutations,
  useAdminUseCasesGraphQL,
} from "../../hooks/useAdminSummaryCheck";
import type { SummaryItem } from "../../hooks/useMyInterviews";
import { logger } from "../../lib/logger";

const CATEGORY_LABELS: Record<string, string> = {
  CHALLENGE: "課題",
  CHANGE: "変化",
  ACHIEVEMENT: "成果",
  SATISFACTION: "満足度の理由",
  RECOMMENDATION: "他者推奨意向",
};

export function AdminSummaryCheck() {
  const navigate = useNavigate();
  const { interviewId } = useParams<{ interviewId: string }>();
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>("");
  const [localSummaryItems, setLocalSummaryItems] = useState<SummaryItem[]>([]);
  const [satisfactionScore, setSatisfactionScore] = useState<number | null>(
    null,
  );
  const [behaviorChangeScore, setBehaviorChangeScore] = useState<number | null>(
    null,
  );
  const [vttCopied, setVttCopied] = useState(false);

  const {
    interview,
    loading: interviewLoading,
    error: interviewError,
  } = useAdminInterviewGraphQL(interviewId || null);

  const {
    summaryItems,
    loading: summaryLoading,
    refetch: refetchSummaryItems,
  } = useAdminInterviewSummaryItemsGraphQL(interviewId || null);

  const { useCases, loading: useCasesLoading } = useAdminUseCasesGraphQL();

  const {
    updateSummaryItem,
    bulkSaveSummaryItems,
    completeSummaryCheck,
    loading: mutationLoading,
  } = useAdminSummaryCheckMutations();

  const loading = interviewLoading || summaryLoading || useCasesLoading;

  useEffect(() => {
    if (summaryItems.length > 0) {
      setLocalSummaryItems(summaryItems);
    }
  }, [summaryItems]);

  useEffect(() => {
    if (interview?.completionReport) {
      setSatisfactionScore(interview.completionReport.satisfactionScore);
      setBehaviorChangeScore(interview.completionReport.behaviorChangeScore);
    }
  }, [interview]);

  useEffect(() => {
    if (interviewError || (!interviewLoading && !interview && interviewId)) {
      alert("インタビューが見つかりません");
      navigate("/employee_interview/interviews");
    }
  }, [interviewError, interviewLoading, interview, interviewId, navigate]);

  const handleCreateManualTemplate = () => {
    if (!interviewId) return;
    const template: SummaryItem[] = [
      {
        summaryItemId: `temp-${crypto.randomUUID()}`,
        interviewId,
        category: "CHALLENGE",
        content: "",
        useCaseId: null,
        order: 0,
        createdAt: null,
        updatedAt: null,
      },
      {
        summaryItemId: `temp-${crypto.randomUUID()}`,
        interviewId,
        category: "CHANGE",
        content: "",
        useCaseId: null,
        order: 1,
        createdAt: null,
        updatedAt: null,
      },
      {
        summaryItemId: `temp-${crypto.randomUUID()}`,
        interviewId,
        category: "ACHIEVEMENT",
        content: "",
        useCaseId: null,
        order: 2,
        createdAt: null,
        updatedAt: null,
      },
      {
        summaryItemId: `temp-${crypto.randomUUID()}`,
        interviewId,
        category: "SATISFACTION",
        content: "",
        useCaseId: null,
        order: 3,
        createdAt: null,
        updatedAt: null,
      },
    ];
    setLocalSummaryItems(template);
  };

  const handleAddChangeItem = () => {
    if (!interviewId) return;
    const changeItems = localSummaryItems.filter(
      (item) => item.category === "CHANGE",
    );
    if (changeItems.length >= 10) return;

    const maxOrder = Math.max(...localSummaryItems.map((item) => item.order));
    const newItem: SummaryItem = {
      summaryItemId: `temp-${crypto.randomUUID()}`,
      interviewId,
      category: "CHANGE",
      content: "",
      useCaseId: null,
      order: maxOrder + 1,
      createdAt: null,
      updatedAt: null,
    };

    const insertIndex = localSummaryItems.findIndex(
      (item) => item.category === "ACHIEVEMENT",
    );
    if (insertIndex === -1) {
      setLocalSummaryItems([...localSummaryItems, newItem]);
    } else {
      const newItems = [...localSummaryItems];
      newItems.splice(insertIndex, 0, newItem);
      setLocalSummaryItems(newItems);
    }
  };

  const handleRemoveChangeItem = (summaryItemId: string) => {
    const changeItems = localSummaryItems.filter(
      (item) => item.category === "CHANGE",
    );
    if (changeItems.length <= 1) return;
    setLocalSummaryItems(
      localSummaryItems.filter((item) => item.summaryItemId !== summaryItemId),
    );
  };

  const handleContentChange = (summaryItemId: string, newContent: string) => {
    setLocalSummaryItems((prev) =>
      prev.map((item) =>
        item.summaryItemId === summaryItemId
          ? { ...item, content: newContent }
          : item,
      ),
    );
  };

  const handleEdit = (item: SummaryItem) => {
    setEditingId(item.summaryItemId);
    setEditContent(item.content);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent("");
  };

  const handleSaveEdit = async (itemId: string) => {
    try {
      setSaving(true);
      const result = await updateSummaryItem({
        summaryItemId: itemId,
        content: editContent,
      });

      if (result) {
        await refetchSummaryItems();
        setEditingId(null);
        setEditContent("");
      } else {
        alert("更新に失敗しました");
      }
    } catch {
      alert("更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleUseCaseChange = (itemId: string, useCaseId: string) => {
    setLocalSummaryItems((prev) =>
      prev.map((item) =>
        item.summaryItemId === itemId
          ? { ...item, useCaseId: useCaseId === "none" ? null : useCaseId }
          : item,
      ),
    );
  };

  const handleBulkSave = async () => {
    if (!interviewId) return;

    try {
      setSaving(true);
      const itemsToUpdate = localSummaryItems.map((item) => ({
        summaryItemId: item.summaryItemId.startsWith("temp-")
          ? undefined
          : item.summaryItemId,
        category: item.category,
        content: item.content,
        useCaseId: item.useCaseId || undefined,
        order: item.order,
      }));

      const result = await bulkSaveSummaryItems({
        interviewId,
        items: itemsToUpdate,
        satisfactionScore,
        behaviorChangeScore,
      });

      if (result?.success) {
        alert("保存しました");
        await refetchSummaryItems();
      } else {
        alert(result?.errorMessage || "保存に失敗しました");
      }
    } catch {
      alert("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyRawVtt = async () => {
    const rawVttText = interview?.rawVttText;
    if (!rawVttText) return;
    try {
      await navigator.clipboard.writeText(rawVttText);
      setVttCopied(true);
      window.setTimeout(() => setVttCopied(false), 2000);
    } catch (err) {
      logger.error(err as Error);
      alert("コピーに失敗しました");
    }
  };

  const handleCompleteSummaryCheck = async () => {
    if (!interviewId) return;

    if (!window.confirm("要約チェックを完了しますか？")) {
      return;
    }

    try {
      setSaving(true);
      const result = await completeSummaryCheck(
        interviewId,
        satisfactionScore,
        behaviorChangeScore,
      );

      if (result?.success) {
        alert("要約チェックを完了しました");
        navigate("/employee_interview/interviews");
      } else {
        alert(result?.errorMessage || "要約チェックの完了に失敗しました");
      }
    } catch {
      alert("要約チェックの完了に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const groupedItems = localSummaryItems.reduce(
    (acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    },
    {} as Record<string, SummaryItem[]>,
  );

  const categoryOrder = [
    "CHALLENGE",
    "CHANGE",
    "ACHIEVEMENT",
    "SATISFACTION",
    "RECOMMENDATION",
  ];

  const getCategoryColor = (category: string) => {
    if (category === "CHALLENGE" || category === "ACHIEVEMENT") {
      return "var(--color-orange-500)";
    }
    return "var(--color-primary-500)";
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-subtle-gray)]">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (!interview) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="mx-auto max-w-[1200px]">
        {/* パンくずリスト */}
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <button
              type="button"
              onClick={() => navigate("/employee_interview/interviews")}
              className="cursor-pointer border-none bg-none p-0 text-[length:inherit] text-[var(--color-primary-500)] no-underline hover:underline"
            >
              インタビュー管理
            </button>
            <span className="text-[#9ca3af]">/</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              要約チェック
            </span>
          </nav>
        </div>

        {/* ヘッダーセクション */}
        <div className="mb-8 flex items-start justify-between">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              要約チェック
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleBulkSave}
              disabled={saving || mutationLoading}
              className={`rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2 font-medium text-white ${saving || mutationLoading ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
            >
              {saving ? "保存中..." : "保存"}
            </button>
            <button
              type="button"
              onClick={handleCompleteSummaryCheck}
              disabled={saving || mutationLoading}
              className={`rounded-md border-none bg-[var(--color-orange-500)] px-4 py-2 font-medium text-white ${saving || mutationLoading ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
            >
              {saving ? "処理中..." : "要約チェック完了"}
            </button>
          </div>
        </div>

        {!interview.completionReport && !interview.stakeholderContext && (
          <div
            role="alert"
            className="mb-6 rounded-md bg-[#fffbeb] p-4 text-[#92400e] text-sm"
          >
            完了報告が未提出です
          </div>
        )}

        {/* インタビュー情報カード */}
        <div className="mb-6 overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
              インタビュー情報
            </h2>
            <div className="flex gap-2">
              {interview.zoomRecordingViewUrl && (
                <a
                  href={interview.zoomRecordingViewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-white px-3 py-1.5 font-medium text-[13px] text-[var(--color-primary-500)] no-underline hover:bg-[var(--color-primary-50)]"
                >
                  録画を見る
                </a>
              )}
              {interview.rawVttText && (
                <button
                  type="button"
                  onClick={handleCopyRawVtt}
                  className="cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-white px-3 py-1.5 font-medium text-[13px] text-[var(--color-primary-500)] hover:bg-[var(--color-primary-50)]"
                >
                  {vttCopied ? "コピーしました" : "文字起こしをコピー"}
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  インタビュー対象者
                </p>
                <p className="m-0 font-medium text-[15px] text-[var(--color-text-primary)]">
                  {interview.candidateName}
                </p>
              </div>
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  企業名
                </p>
                <p className="m-0 font-medium text-[15px] text-[var(--color-text-primary)]">
                  {interview.sponsorName || "-"}
                </p>
              </div>
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  ターム
                </p>
                <p className="m-0 font-medium text-[15px] text-[var(--color-text-primary)]">
                  {interview.termTitle}
                </p>
              </div>
              {interview.stakeholderContext && (
                <>
                  <div>
                    <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                      受け取る人
                    </p>
                    <p className="m-0 font-medium text-[15px] text-[var(--color-text-primary)]">
                      {interview.stakeholderContext.recipientNames.join("、")}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                      使った設定
                    </p>
                    <p className="m-0 font-medium text-[15px] text-[var(--color-text-primary)]">
                      {interview.stakeholderContext.stakeholderSettingName}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* スコア評価カード */}
        <div className="mb-6 overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
              スコア評価
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="satisfaction-score"
                  className="mb-2 block font-medium text-[13px] text-[var(--color-text-secondary)]"
                >
                  満足度スコア
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((score) => (
                    <button
                      key={score}
                      type="button"
                      onClick={() => setSatisfactionScore(score)}
                      className={`h-12 w-12 cursor-pointer rounded-lg font-bold text-lg ${
                        satisfactionScore === score
                          ? "border-2 border-[var(--color-primary-500)] bg-[var(--color-primary-500)] text-white"
                          : "border border-[var(--color-border-default)] bg-white text-[var(--color-text-primary)]"
                      }`}
                    >
                      {score}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label
                  htmlFor="behavior-change-score"
                  className="mb-2 block font-medium text-[13px] text-[var(--color-text-secondary)]"
                >
                  行動変容スコア
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((score) => (
                    <button
                      key={score}
                      type="button"
                      onClick={() => setBehaviorChangeScore(score)}
                      className={`h-12 w-12 cursor-pointer rounded-lg font-bold text-lg ${
                        behaviorChangeScore === score
                          ? "border-2 border-[var(--color-primary-500)] bg-[var(--color-primary-500)] text-white"
                          : "border border-[var(--color-border-default)] bg-white text-[var(--color-text-primary)]"
                      }`}
                    >
                      {score}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 要約内容カード */}
        <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
              要約内容
            </h2>
          </div>
          <div className="p-6">
            {localSummaryItems.length === 0 ? (
              <div className="p-12 text-center">
                <p className="mb-4 text-[#9ca3af]">
                  要約データがまだありません。動画解析が完了するまでお待ちください。
                </p>
                <button
                  type="button"
                  onClick={handleCreateManualTemplate}
                  className="cursor-pointer rounded-md border-none bg-[var(--color-primary-500)] px-6 py-2.5 font-medium text-sm text-white"
                >
                  手動で要約を作成
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {categoryOrder.map((category) => {
                  const items = groupedItems[category] || [];
                  if (items.length === 0) return null;

                  const categoryColor = getCategoryColor(category);

                  return (
                    <div key={category}>
                      {/* カテゴリヘッダー */}
                      <div className="mb-4 flex items-center justify-between border-[var(--color-border-default)] border-b-2 pb-3">
                        <h3
                          className="m-0 border-l-4 pl-4 font-bold text-lg"
                          style={{
                            color: categoryColor,
                            borderLeftColor: categoryColor,
                          }}
                        >
                          {CATEGORY_LABELS[category]}
                          {category === "CHANGE" && ` (${items.length}件)`}
                        </h3>
                        {category === "CHANGE" && items.length < 10 && (
                          <button
                            type="button"
                            onClick={handleAddChangeItem}
                            className="cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-transparent px-3 py-1 font-medium text-[13px] text-[var(--color-primary-500)]"
                          >
                            + 追加
                          </button>
                        )}
                      </div>

                      {/* カテゴリアイテム */}
                      <div className="flex flex-col gap-4">
                        {items.map((item) => (
                          <div
                            key={item.summaryItemId}
                            className="rounded-lg border border-[var(--color-border-default)] bg-white p-4"
                          >
                            <div>
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                  {item.summaryItemId.startsWith("temp-") ||
                                  item.content === "" ? (
                                    <textarea
                                      value={item.content}
                                      onChange={(e) =>
                                        handleContentChange(
                                          item.summaryItemId,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={`${CATEGORY_LABELS[item.category]}を入力してください`}
                                      rows={4}
                                      className="box-border w-full resize-y rounded-lg border border-[var(--color-border-default)] bg-white p-3 text-[var(--color-text-primary)] text-sm outline-none focus:border-[var(--color-primary-500)]"
                                    />
                                  ) : editingId === item.summaryItemId ? (
                                    <div>
                                      <textarea
                                        value={editContent}
                                        onChange={(e) =>
                                          setEditContent(e.target.value)
                                        }
                                        rows={6}
                                        className="box-border w-full resize-y rounded-lg border border-[var(--color-border-default)] bg-white p-3 text-[var(--color-text-primary)] text-sm outline-none focus:border-[var(--color-primary-500)]"
                                      />
                                      <div className="mt-2 flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleSaveEdit(item.summaryItemId)
                                          }
                                          disabled={saving}
                                          className={`rounded-md border-none bg-[var(--color-primary-500)] px-3 py-1.5 font-medium text-[13px] text-white ${saving ? "cursor-not-allowed" : "cursor-pointer"}`}
                                        >
                                          保存
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleCancelEdit}
                                          className="cursor-pointer rounded-md border border-[var(--color-text-secondary)] bg-transparent px-3 py-1.5 font-medium text-[13px] text-[var(--color-text-secondary)]"
                                        >
                                          キャンセル
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="whitespace-pre-wrap break-words text-[var(--color-text-primary)] text-sm leading-[1.8]">
                                      {item.content}
                                    </div>
                                  )}
                                </div>
                                {editingId !== item.summaryItemId &&
                                  !item.summaryItemId.startsWith("temp-") &&
                                  item.content !== "" && (
                                    <button
                                      type="button"
                                      onClick={() => handleEdit(item)}
                                      className="shrink-0 cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-transparent px-3 py-1.5 font-medium text-[13px] text-[var(--color-primary-500)]"
                                    >
                                      編集
                                    </button>
                                  )}
                                {category === "CHANGE" &&
                                  localSummaryItems.filter(
                                    (i) => i.category === "CHANGE",
                                  ).length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveChangeItem(
                                          item.summaryItemId,
                                        )
                                      }
                                      className="shrink-0 cursor-pointer rounded-md border border-[#dc2626] bg-transparent px-3 py-1.5 font-medium text-[#dc2626] text-[13px]"
                                    >
                                      削除
                                    </button>
                                  )}
                              </div>

                              {/* ユースケース選択 */}
                              {category === "CHANGE" && (
                                <div className="mt-3 border-[var(--color-border-default)] border-t pt-3">
                                  <label
                                    htmlFor={`use-case-${item.summaryItemId}`}
                                    className="mb-2 block font-semibold text-[13px] text-[var(--color-text-primary)]"
                                  >
                                    ユースケース
                                  </label>
                                  <select
                                    id={`use-case-${item.summaryItemId}`}
                                    value={item.useCaseId || "none"}
                                    onChange={(e) =>
                                      handleUseCaseChange(
                                        item.summaryItemId,
                                        e.target.value,
                                      )
                                    }
                                    disabled={saving}
                                    className={`w-full max-w-xs appearance-none rounded-lg border border-[var(--color-border-default)] bg-[position:right_12px_center] bg-[url("data:image/svg+xml,%3Csvg_width='12'_height='12'_viewBox='0_0_12_12'_fill='none'_xmlns='http://www.w3.org/2000/svg'%3E%3Cpath_d='M3_4.5L6_7.5L9_4.5'_stroke='%23666'_stroke-width='1.5'_stroke-linecap='round'_stroke-linejoin='round'/%3E%3C/svg%3E")] bg-white bg-no-repeat py-2 pr-9 pl-3 text-[var(--color-text-primary)] text-sm outline-none ${saving ? "cursor-not-allowed" : "cursor-pointer"}`}
                                  >
                                    <option value="none">未選択</option>
                                    {useCases.map((uc) => (
                                      <option
                                        key={uc.useCaseId}
                                        value={uc.useCaseId}
                                      >
                                        {uc.category} / {uc.cognitiveAction}:{" "}
                                        {uc.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
